import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Check, X, Pencil, Clock } from "@/lib/icons";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABEL = { reward: "Reward", rubric: "Habit rubric", insight: "Pattern insight" };

function EditForm({ item, onSubmit, onCancel }) {
  const [title, desc, action] = item.text.split("\n");
  const [rep, setRep] = useState({ title: title || "", description: desc || "", action: action || "" });
  const cls = "w-full px-3 py-2 rounded-xl border border-stone-300 text-sm outline-none focus:border-amber-600";
  return (
    <div className="mt-3 space-y-2" data-testid={`ai-review-edit-form-${item.id}`}>
      <input value={rep.title} onChange={(e) => setRep({ ...rep, title: e.target.value })} className={cls} placeholder="Title" data-testid={`ai-review-edit-title-${item.id}`} />
      <textarea value={rep.description} onChange={(e) => setRep({ ...rep, description: e.target.value })} rows={2} className={cls} placeholder="Description" data-testid={`ai-review-edit-desc-${item.id}`} />
      <input value={rep.action} onChange={(e) => setRep({ ...rep, action: e.target.value })} className={cls} placeholder="Action to claim it" data-testid={`ai-review-edit-action-${item.id}`} />
      <div className="flex gap-2">
        <button onClick={() => onSubmit(rep)} data-testid={`ai-review-edit-save-${item.id}`} className="amber-btn text-xs py-2">Replace & approve</button>
        <button onClick={onCancel} className="text-xs font-semibold text-stone-500 px-3">Cancel</button>
      </div>
    </div>
  );
}

function RejectForm({ item, reasons, onSubmit, onCancel }) {
  const [reason, setReason] = useState("unhelpful");
  const [note, setNote] = useState("");
  return (
    <div className="mt-3 space-y-2" data-testid={`ai-review-reject-form-${item.id}`}>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(reasons).map(([k, label]) => (
          <button key={k} onClick={() => setReason(k)} data-testid={`ai-review-reason-${k}-${item.id}`}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold ${reason === k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>{label}</button>
        ))}
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid={`ai-review-note-${item.id}`}
        placeholder="Optional note the member will see (kept gentle)" className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm outline-none focus:border-amber-600" />
      <div className="flex gap-2">
        <button onClick={() => onSubmit(reason, note)} data-testid={`ai-review-reject-confirm-${item.id}`} className="text-xs font-semibold px-4 py-2 rounded-full bg-red-700 text-white hover:bg-red-800">Withdraw it</button>
        <button onClick={onCancel} className="text-xs font-semibold text-stone-500 px-3">Cancel</button>
      </div>
    </div>
  );
}

function ReviewItem({ item, reasons, onAct }) {
  const [mode, setMode] = useState(null);
  const held = item.status === "held";
  return (
    <li className={`rounded-2xl border p-4 ${held ? "border-red-200 bg-red-50/60" : "border-stone-200 bg-white"} ${item.overdue ? "ring-2 ring-amber-300" : ""}`} data-testid={`ai-review-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[10px] font-bold uppercase tracking-widest">
            <span className="text-stone-500">{KIND_LABEL[item.kind] || item.kind}</span>
            <span className="text-amber-800">{item.client_name}</span>
            {item.habit_title && <span className="text-stone-400 normal-case tracking-normal font-medium">· {item.habit_title}</span>}
            {held && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800" data-testid={`ai-review-held-${item.id}`}><ShieldAlert className="w-3 h-3" /> Held — {item.flag_labels.join(", ")}</span>}
            {item.overdue && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Clock className="w-3 h-3" /> {item.age_days}d — AI paused for this person</span>}
          </div>
          <p className="text-sm text-stone-800 mt-2 whitespace-pre-line" data-testid={`ai-review-text-${item.id}`}>{item.text}</p>
          <div className="text-[11px] text-stone-400 mt-2">Signals sent: {item.signals.join(", ") || "none"} · {item.provider} ({item.model}) · {item.lane === "phi" ? "BAA lane" : "general lane"}</div>
        </div>
        {!mode && (
          <div className="flex gap-1.5 shrink-0">
            <button onClick={() => onAct(item, { action: "approve" })} data-testid={`ai-review-approve-${item.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"><Check className="w-3.5 h-3.5" /> Approve</button>
            {item.kind === "reward" && <button onClick={() => setMode("edit")} data-testid={`ai-review-edit-${item.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
            <button onClick={() => setMode("reject")} data-testid={`ai-review-reject-${item.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-stone-300 text-stone-700 hover:bg-red-50 hover:text-red-800"><X className="w-3.5 h-3.5" /> Reject</button>
          </div>
        )}
      </div>
      {mode === "edit" && <EditForm item={item} onCancel={() => setMode(null)} onSubmit={(replacement) => onAct(item, { action: "edit", replacement })} />}
      {mode === "reject" && <RejectForm item={item} reasons={reasons} onCancel={() => setMode(null)} onSubmit={(reason, note) => onAct(item, { action: "reject", reason, note })} />}
    </li>
  );
}

export function AiReviewInbox({ onChanged }) {
  const [data, setData] = useState(null);
  const [phiOn, setPhiOn] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(async () => {
    const [r, st] = await Promise.all([api.get("/clinician/ai-review"), api.get("/ai/status").catch(() => null)]);
    setData(r.data);
    if (st) setPhiOn(st.data.phi_ai_enabled !== false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (item, body) => {
    try {
      await api.post(`/clinician/ai-review/${item.id}`, body);
      toast.success({ approve: "Approved.", reject: "Withdrawn — the member sees a gentle note and can ask for another.", edit: "Replaced with your version." }[body.action]);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that review");
    }
  };
  const bulk = async () => {
    const ids = data.items.filter((i) => i.status === "pending").map((i) => i.id);
    const r = await api.post("/clinician/ai-review/bulk-approve", { ids });
    toast.success(`Approved ${r.data.approved} routine suggestion${r.data.approved === 1 ? "" : "s"}.`);
    await load();
    onChanged?.();
  };

  if (!data) return null;
  const { summary, items } = data;
  const open = summary.pending + summary.held;
  const visible = showAll ? items : items.slice(0, 6);
  return (
    <section className="warm-card p-6 mb-6" data-testid="ai-review-inbox">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <h3 className="font-display font-bold text-stone-900">AI suggestions to review</h3>
            <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-stone-900 text-white" data-testid="ai-review-count">{open}</span>
          </div>
          {!phiOn && <p className="text-xs text-stone-500 mt-1 italic" data-testid="phi-ai-off-note">AI for the people you support is switched off here. Nothing new will arrive here until it is turned on.</p>}
          <p className="text-xs text-stone-500 mt-1">
            The people you support see routine suggestions right away; you review them after the fact. Anything the safety screen flags is <strong>held</strong> until you approve it.
            After {data.max_days} days unreviewed, AI pauses for that person. {summary.reviewed_30d > 0 && `You've reviewed ${summary.reviewed_30d} in the last 30 days.`}
          </p>
        </div>
        {summary.pending > 1 && <button onClick={bulk} data-testid="ai-review-bulk-approve" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Approve all {summary.pending} routine</button>}
      </div>
      {open === 0 ? (
        <div className="text-sm text-stone-500 mt-4" data-testid="ai-review-empty">Nothing waiting. New AI suggestions for the people you support will appear here.</div>
      ) : (
        <ul className="space-y-3 mt-4">
          {visible.map((i) => <ReviewItem key={i.id} item={i} reasons={data.reasons} onAct={act} />)}
        </ul>
      )}
      {items.length > 6 && <button onClick={() => setShowAll((v) => !v)} data-testid="ai-review-toggle-all" className="mt-3 text-xs font-semibold text-amber-800">{showAll ? "Show fewer" : `Show all ${items.length}`}</button>}
    </section>
  );
}
